import { useEffect, useState, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select, { components } from 'react-select';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Spinner,
} from 'reactstrap';
import i18next from 'i18next';
import dayjs from 'dayjs';
import FullCalendar from '@fullcalendar/react';
import BootstrapTheme from '@fullcalendar/bootstrap';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ruLocale from '@fullcalendar/core/locales/ru';
import uaLocale from '@fullcalendar/core/locales/uk';
import { CONSULTATIONS_STATUSES, CONSULTATIONS_TYPES_ARRAY } from '@/configs';
import { MODALS } from '@/configs/modals';
import { useModals } from '@/hooks/useModals';
import {
  getDate,
  normalizeSchedulers,
  isArray,
  isEmpty,
  get,
} from '@/utils/helpers';
import { getConsultationPageData } from '@/utils/selectors';
import { fetchPsychologistSchedulerApi } from '@/store/psychologist/actions';
import { fetchUsersListApi } from '@/store/users/actions';
import { fetchPsychologistsApi } from '@/store/psychologists/actions';
import {
  cancelConsultation,
  fetchConsultation,
  submitConsultation,
} from '@/store/consultations/consultationsActions';
import { clearPage } from '@/store/base/actions';
import { ListItem, ListItems } from '@/components/ListItems';
import { fetchUserApi } from '@/store/user/actions';
import UserConsultationBookingDrawer from '@/views/UserConsultationBookingDrawer';

const normalizeInitSchedule = (initSchedule, initPsychologistId) => {
  const schedule = {
    id: initSchedule?.id,
    start_datetime: initSchedule?.start,
    end_datetime: initSchedule?.end,
    taken: true,
    psychologist: initPsychologistId,
  };
  return schedule;
};

const PsychologistConsultationManageDrawer = memo(() => {
  const modalId = MODALS.consultation;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { params, modalOpen, modalClose } = useModals(modalId);
  const {
    isOpen,
    id,
    initSchedule,
    initPsychologistId,
    initPsychologistName,
    initUser,
  } = params || {};
  const isEdit = !!id;

  const {
    data: consultation,
    isLoading,
    isLoaded,
  } = useSelector(getConsultationPageData);

/**-------------------------------
 * states
 *-----------------------------*/
  const [typeOptions, setTypeOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClientsFetching, setIsClientsFetching] = useState(false);
  const [clientsOptions, setClientsOptions] = useState([]);
  const [isPsychologistsFetching, setIsPsychologistsFetching] = useState(false);
  const [isConsultationTypesFetching, setIsConsultationTypesFetching] = useState(false);
  const [psychologistsOptions, setPsychologistsOptions] = useState([]);
  const [isSchedulerFetching, setIsSchedulerFetching] = useState(false);
  const [schedulers, setSchedulers] = useState([]);
  const [selectedPsychologist, setSelectedPsychologist] = useState();
  const [selectedScheduler, setSelectedScheduler] = useState();
  const [selectedType, setSelectedType] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isVisiblePshSelect, setIsVisiblePshSelect] = useState(false);
  const [isVisibleTypeSelect, setIsVisibleTypeSelect] = useState(false);
  const [isVisibleUserSelect, setIsVisibleUserSelect] = useState(false);
  const [isChangedPsh, setIsChangedPsh] = useState(false);

/**-------------------------------
 * handlers
 *-----------------------------*/
  const toggleCanvas = () => {
    if (isOpen) {
      setClientsOptions([]);
      setPsychologistsOptions([]);
      setSchedulers([]);
      setSelectedPsychologist(null);
      setSelectedScheduler(null);
      setSelectedClient(null);
      setSelectedType(null);
      dispatch(clearPage('consultation'));
      setIsSubmitting(false);
      setIsChangedPsh(false);
      setTypeOptions([]);
      setIsVisiblePshSelect(false);
      setIsVisibleTypeSelect(false);
      setIsConsultationTypesFetching(false);
      setIsSchedulerFetching(false);
      setIsVisibleUserSelect(false);
      setIsPsychologistsFetching(false);
      setIsClientsFetching(false);
      modalClose(modalId);
    }
  };

  const fetchPsychologists = async () => {
    if (!isOpen) return;
    setIsPsychologistsFetching(true);
    const psychologists = await fetchPsychologistsApi();
    setPsychologistsOptions(
      psychologists?.map(({ fullname, fullname_uk, fullname_ru, id }) => ({
        label: fullname || fullname_uk || fullname_ru,
        value: id,
      }))
    );
    setIsPsychologistsFetching(false);
  };

  const fetchPsychologistScheduler = useCallback(
    async (psychologistId) => {
      if (!psychologistId) return;
      if (!isOpen) return;
      // const _schedule = !isEmpty(initSchedule)
      //   ? normalizeInitSchedule(initSchedule, initPsychologistId)
      //   : consultation?.schedule;
      // const _psychologistId =
      //   initPsychologistId || consultation?.psychologist?.id;

      setIsSchedulerFetching(true);
      const schedulersData = await fetchPsychologistSchedulerApi(psychologistId);
      const { schedulers: _schedulers } = schedulersData || {};
      const normalizedSchedulers = _schedulers.map((i) =>
        normalizeSchedulers({ ...i, taken: Number(initSchedule?.id) === Number(i?.id) })
      );
      // const currentScheduler =
      //   !isEmpty(_schedule) &&
      //   Number(_psychologistId) === Number(psychologistId)
      //     ? normalizeSchedulers({ ..._schedule, taken: !isChangedPsh }, 'psh')
      //     : [];
      // setSchedulers([...normalizedSchedulers, currentScheduler]);
      setSchedulers(normalizedSchedulers);
      setIsSchedulerFetching(false);
    },
    [
      isOpen,
      // consultation?.schedule,
      // consultation?.psychologist?.id,
      initSchedule,
      // initPsychologistId,
      // isChangedPsh,
    ]
  );

  const fetchClients = async () => {
    if (!isOpen) return;
    setIsClientsFetching(true);
    try {
      const users = await fetchUsersListApi();
      const clients =
        !isEmpty(users) && isArray(users)
          ? users?.map(({ fullname, username, id }) => ({
              label: fullname + ' (' + username + ')',
              value: id,
            }))
          : [];
      setClientsOptions(clients);
    } catch (err) {}
    finally {
      setIsClientsFetching(false);
    }
  };

  const fetchClient = async (userId) => {
    setIsConsultationTypesFetching(true);
    if (!isOpen) return;
    try {
      const user = await fetchUserApi(userId);
      setAvailableTypeOptions(user);
    } catch (err) {}
  };

  const setAvailableTypeOptions = (user) => {
    if (!isOpen || isEmpty(user)) return;
    const consultations = user?.consultations?.reduce((a, c) => {
      if (c.status === CONSULTATIONS_STATUSES.bought) {
        a[c?.service_type] = (a[c?.service_type] || 0) + 1;
      }
      return a
    }, {});
    const options = CONSULTATIONS_TYPES_ARRAY.map((i) => ({
      label: t(`consultations.types.${i}`),
      value: i,
      isDisabled: !consultations[i],
      count: consultations[i],
    }));

    setTypeOptions(options);
    setIsConsultationTypesFetching(false);
  }

  const handleChangeType = (item) => {
    setSelectedType(item);
  };

  const handleChangeClient = (item) => {
    setIsConsultationTypesFetching(true);
    setSelectedClient(item);
    setSelectedType(null);
    fetchClient(item?.value);
    setIsVisibleTypeSelect(true);
  };

  const handleChangePsychologist = (item) => {
    setSelectedPsychologist(item);
    setSelectedScheduler(null);
    setIsChangedPsh(true);
    fetchPsychologistScheduler(item?.value);
  };

  const handleEventClick = (arg) => {
    const event = arg.event;
    const selectedEvent = {
      id: event?.id,
      start: event?.start,
      end: event?.end,
      psychologist: event?.psychologist,
    };
    setSelectedScheduler(selectedEvent);
    const filteredSchedulers = schedulers.map((i) => {
      if (Number(i.id) === Number(selectedEvent.id)) {
        i.taken = true;
      } else {
        i.taken = false;
      }
      return normalizeSchedulers(i, 'psh');
    });
    setSchedulers(filteredSchedulers);
  };

  const handleClickSubmit = async () => {
    if (!selectedScheduler?.id) {
      toast.error(t('consultation.modal.error_no_sheduler'));
      return;
    }
    if (!selectedType) {
      toast.error(t('consultation.modal.error_no_type'));
      return;
    }
    setIsSubmitting(true);
    const data = {
      scheduler: selectedScheduler?.id,
      services_type: selectedType?.value || selectedType,
      user: selectedClient?.value || selectedClient?.id,
    };
    console.log('data', data);

    try {
      await submitConsultation({
        id,
        data,
        dispatch,
        fromSheduleId: consultation?.schedule?.id,
        onSuccess: () => {
          toast.success(
            t(
              isEdit
                ? 'consultation.modal.update_success'
                : 'consultation.modal.create_success'
            )
          );
          toggleCanvas();
          !isEmpty(initSchedule) && modalClose(MODALS.schedulerManage);
        },
        onError: (error) => {
          const errors = get(error, ['errors'], {});
          const normalizeErrors = Object.values(errors).join(' ');
          toast.error(
            !isEmpty(errors)
              ? normalizeErrors
              : t('consultation.modal.update_error')
          );
        },
      });
    } catch (err) {} finally {
      setIsSubmitting(false);
    }
  };

  const handleClickChangePsh = () => {
    setIsVisiblePshSelect(true);
    fetchPsychologists();
  };

  const handleClickChangeType = () => {
    setIsVisibleTypeSelect(true);
  };

  const handleClickCancelConsultation = () => {
    modalOpen(MODALS.confirm, {
      isOpen: true,
      text: t('consultation.confirm_delete_text', { id: consultation?.id }),
      isDeleteType: true,
      onOk: () => {
        setIsSubmitting(true);
        dispatch(
          cancelConsultation(consultation?.id, {
            t,
            scheduleId: consultation?.schedule?.id,
            onSuccess: () => toggleCanvas(),
          })
        );
      },
    });
  };

  const CustomOptionForType = ({ children, ...props }) => (
    <components.Option {...props}>
      {children}
      {!!props?.data?.count && <span className="select-option-badge">{props?.data?.count}</span>}
    </components.Option>
  );

/**-------------------------------
 * useEffects
 *-----------------------------*/
  useEffect(() => {
    !!id && !!isOpen && dispatch(fetchConsultation(id));
  }, [id, dispatch, isOpen]);

  useEffect(() => {
    if (!isEmpty(consultation) && isOpen) {
      if (!!consultation?.psychologist) {
        setSelectedPsychologist({
          label: consultation?.psychologist?.fullname,
          value: consultation?.psychologist?.id,
        });
      }
      if (!!consultation?.schedule?.id) {
        setSelectedScheduler(
          normalizeSchedulers(consultation?.schedule, 'psh')
        );
      }
      if (!!consultation?.user?.id) {
        setSelectedClient(consultation?.user);
        fetchClient(consultation?.user?.id);
      }
      if (!!consultation?.service?.type) {
        setSelectedType(consultation?.service?.type);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultation, isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (!isEmpty(initSchedule) && !!initPsychologistId) {
        const schedule = normalizeInitSchedule(
          initSchedule,
          initPsychologistId
        );
        setSelectedScheduler(normalizeSchedulers(schedule, 'psh'));
      }

      if (!!initPsychologistId) {
        setSelectedPsychologist({
          label: initPsychologistName || '-',
          value: initPsychologistId,
        });
      }
    }
  }, [isOpen, initSchedule, initPsychologistId, initPsychologistName]);

  useEffect(() => {
    if (isOpen && !isEmpty(initUser)) {
      setSelectedClient(initUser);
      setAvailableTypeOptions(initUser);
      setIsVisibleTypeSelect(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initUser]);

  useEffect(() => {
    if (isOpen && isEmpty(initUser) && !id) {
      console.log('useEffect fetchClients');
      fetchClients();
      setIsVisibleUserSelect(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initUser, id]);

  useEffect(() => {
    if (isOpen && isEmpty(initSchedule) && !initPsychologistId && !id) {
      setIsVisiblePshSelect(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initPsychologistId, initSchedule, id]);

  useEffect(() => {
    if (isOpen && !initPsychologistId && !id) {
      fetchPsychologists();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initPsychologistId, id]);

  useEffect(() => {
    if (isOpen && selectedPsychologist?.value) {
      fetchPsychologistScheduler(selectedPsychologist?.value);
    }
  }, [isOpen, fetchPsychologistScheduler, selectedPsychologist?.value]);

  // if (isOpen) {
  // console.group('ConsultationManageDrawer');
  // console.log('--------- INIT VALUES ---------');
  // console.log('id', id);
  // console.log('initSchedule', initSchedule);
  // console.log('initUser', initUser);
  // console.log('initPsychologistId', initPsychologistId);
  // console.log('initPsychologistName', initPsychologistName);
  // console.log('--------- IS VALUES ---------');
  // console.log('isVisibleUserSelect', isVisibleUserSelect);
  // console.log('--------- SELECTED VALUES ---------');
  // console.log('selectedPsychologist', selectedPsychologist);
  // console.log('selectedScheduler', selectedScheduler);
  // console.log('selectedClient', selectedClient);
  // console.log('--------- FETCHED VALUES ---------');
  // console.log('consultation', consultation);
  // console.groupEnd();
  // }

  if (!isOpen) return null;

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggleCanvas}
      direction="end"
      className={modalId}
    >
      <OffcanvasHeader toggle={toggleCanvas}>
        {isEdit
          ? t('consultation.modal.edit_title', { id: consultation?.id })
          : t('consultation.modal.add_title')}
      </OffcanvasHeader>
      <OffcanvasBody>
        {!!isLoading && !isLoaded && !!isEdit && (
          <Spinner color="primary" type="grow" />
        )}
        {(!isEdit || isLoaded) && (
          <div className="pos-r">
            {isSubmitting && (
              <div className="calendar-scheduler-spinner is-submitting">
                <Spinner color="primary" type="grow" />
              </div>
            )}
            <ListItems
              layout="horizontal"
              className="modal-consultation-resume mb-4"
            >
              <ListItem
                label={t('consultation.modal.client')}
                value={
                  isVisibleUserSelect ? (
                    <Select
                      placeholder={t('consultation.modal.select_client')}
                      options={clientsOptions}
                      value={selectedClient}
                      onChange={handleChangeClient}
                      isLoading={isClientsFetching}
                      className="select2-selection"
                      classNamePrefix="select2-selection"
                      noOptionsMessage={() => t('no_data')}
                    />
                  ) : (
                    selectedClient?.fullname || '-'
                  )
                }
              />
              <ListItem
                label={t('consultation.modal.type')}
                value={
                  isVisibleTypeSelect ? (
                    <Select
                      placeholder={
                        selectedType
                          ? t(`consultations.types.${selectedType}`)
                          : t('consultation.modal.select_type')
                      }
                      options={typeOptions}
                      value={selectedType}
                      onChange={handleChangeType}
                      isLoading={isConsultationTypesFetching}
                      className="select2-selection"
                      classNamePrefix="select2-selection"
                      noOptionsMessage={() => t('no_data')}
                      components={{ Option: CustomOptionForType }}
                    />
                  ) : (isConsultationTypesFetching && !selectedType ? <Spinner color="primary" type="grow" size="sm" /> : (!!selectedType ? t(`consultations.types.${selectedType}`) : '-'))
                }
                extra={
                  !isVisibleTypeSelect &&
                  isEdit && (
                    <Button
                      color="info"
                      type="button"
                      className="btn-icon btn-icon--sm ml-3"
                      size="sm"
                      title={t('consultation.modal.btn_change_type')}
                      onClick={handleClickChangeType}
                    >
                      <i className="fas fa-edit" />
                    </Button>
                  )
                }
              />
              <ListItem
                label={t('consultation.modal.psh')}
                value={
                  isVisiblePshSelect ? (
                    <Select
                      placeholder={
                        selectedPsychologist?.label ||
                        t('consultation.modal.select_psychologist')
                      }
                      options={psychologistsOptions}
                      value={selectedPsychologist}
                      onChange={handleChangePsychologist}
                      isLoading={isPsychologistsFetching}
                      className="select2-selection"
                      classNamePrefix="select2-selection"
                      noOptionsMessage={() => t('no_data')}
                    />
                  ) : (
                    selectedPsychologist?.label ||
                    consultation?.psychologist?.fullname ||
                    '-'
                  )
                }
                extra={
                  !isVisiblePshSelect && (
                    <Button
                      color="info"
                      type="button"
                      className="btn-icon btn-icon--sm ml-3"
                      size="sm"
                      title={t('consultation.modal.btn_change_psh')}
                      onClick={handleClickChangePsh}
                    >
                      <i className="fas fa-edit" />
                    </Button>
                  )
                }
              />
              <ListItem
                label={t('consultation.modal.date')}
                value={
                  <span className="font-secondary fz-90">
                    {!!selectedScheduler ? (
                      <>
                        {getDate(selectedScheduler?.start, 'DD.MM.YYYY')},{' '}
                        {getDate(selectedScheduler?.start, 'HH:mm')}-
                        {getDate(selectedScheduler?.end, 'HH:mm')}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                }
              />
            </ListItems>
            <div className="calendar-scheduler">
              {isSchedulerFetching && <Spinner color="primary" type="grow" />}
              {!isSchedulerFetching && !isEmpty(schedulers) && (
                <FullCalendar
                  plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin]}
                  initialDate={getDate(dayjs(), 'YYYY-MM-DD')}
                  slotDuration={'01:00:00'}
                  handleWindowResize={true}
                  themeSystem="bootstrap"
                  headerToolbar={{
                    left: 'prev,next',
                    center: 'title',
                    right: 'today',
                  }}
                  events={schedulers}
                  eventClick={handleEventClick}
                  locale={i18next.language === 'ru' ? ruLocale : uaLocale}
                  eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                  }}
                  eventContent={(info) => {
                    return { html: info.event.title };
                  }}
                  className="calendar-scheduler"
                />
              )}
            </div>
          </div>
        )}
        <div className="modal-consultation-footer">
          <div className="mt-3 flex jcsb">
            {isEdit && (
              <Button
                color="danger"
                className="waves-effect waves-light mr-1 px-3"
                type="button"
                disabled={isSubmitting}
                onClick={handleClickCancelConsultation}
              >
                <span>{t('consultation.modal.btn_cancel')}</span>
              </Button>
            )}
            <Button
              color="primary"
              className="waves-effect waves-light px-3 ml-a"
              type="button"
              disabled={isSubmitting}
              onClick={handleClickSubmit}
            >
              {isSubmitting && <Spinner size="sm" className="mr-2" />}
              <span>{t('consultation.modal.btn_save')}</span>
            </Button>
          </div>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
});

const ConsultationManageDrawer = () => {
  const { params, modalClose } = useModals(MODALS.consultation);
  if (params?.isOpen && params?.initUser?.id && params?.allowSpecialistTypeSelection && !params?.id && !params?.initSchedule && !params?.initPsychologistId) {
    return <UserConsultationBookingDrawer
      key={params.initUser?.id}
      user={params.initUser}
      onClose={() => modalClose(MODALS.consultation)}
    />;
  }
  return <PsychologistConsultationManageDrawer />;
};

export default ConsultationManageDrawer;
